import React, { useEffect, useRef, useState } from "react";
import { Formik, Field, ErrorMessage, FormikErrors, FormikProps } from "formik";
import { Button } from "Components/Button";
import Input from "Components/Form/Input";
import { CampFile, ScheduleContainer } from "Pages/SchedulePartner/styles";
import ImportDocument from "Assets/Images/import.svg";
import { InputPhone } from "Components/Form/PhoneInput";
import { validationSchema } from "Pages/SchedulePartner/schemas";
import { useScheduleContext } from "Hooks/useScheduleContext";
import { useQuery } from "react-query";
import { validateCEP } from "Requests/cep";
import { Box } from "styles/globalComponents";
import { getPartnersCombos } from "Requests/SchedulePartners";
import Select from "Components/Form/Select";
import { validateArrayType } from "Utils/Arrays";
import TrashIcon from "Assets/Images/trashIcon.png";

export type SetField = (
  field: string,
  value: any,
  shouldValidate?: boolean
) => Promise<void | FormikErrors<any>>;

export type FormValues = {
  email: string;
  name: string;
  attach: File | null;
  cpf: string;
  birthDate: string;
  phone: string;
  weight: string;
  height: string;
  gender: string;
  motherName: string;
  cep: string;
  street: string;
  number: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
  obs: string;
};

const initialValues = {
  name: "",
  email: "",
  cpf: "",
  birthDate: "",
  phone: "",
  weight: "",
  height: "",
  gender: "",
  motherName: "",
  cep: "",
  street: "",
  number: "",
  complement: "",
  neighborhood: "",
  city: "",
  state: "",
  attach: null,
  obs: "",
} as const;

export const RegisterPersonalInfo = () => {
  const {
    setFiles,
    deletFilesToUpload,
    cep,
    setPersonalInfoFn,
    appointmentId,
    resetSchedule,
  } = useScheduleContext();
  const [imagesPreview, setImagesPreview] = useState<string[] | []>([]);

  const combos = useQuery({
    queryFn: getPartnersCombos,
    queryKey: ["genders-partners"],
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const { appointments, genders } = combos.data ?? {};

  const appointmentSelected = appointments?.find(
    (appointment) => appointment.id === appointmentId
  );

  const cepInfo = useQuery({
    queryFn: () => validateCEP(cep),
    queryKey: ["cep", cep],
    enabled: cep.length === 9,
  });

  const formikRef = useRef<FormikProps<any>>(null);

  const handleSubmit = async (values: FormValues) => {
    setPersonalInfoFn(values);
  };

  const addAttachment = (
    e: React.ChangeEvent<HTMLInputElement>,
    setField: SetField
  ) => {
    if (e.target.files && e.target.files.length >= 1) {
      const file = e.target.files[0];
      setField("attach", file);
      setImagesPreview((prev: string[]) => [
        ...prev,
        URL.createObjectURL(file),
      ]);
      setFiles(file);
    }
  };

  const deleteFiles = (indexToRemove: number, setField: SetField) => {
    const files = imagesPreview.filter((_, index) => index !== indexToRemove);
    setImagesPreview(files);
    deletFilesToUpload(indexToRemove);

    if (files.length === 0) setField("attach", null);
  };

  useEffect(() => {
    if (cepInfo?.data?.logradouro && formikRef.current) {
      const { bairro, logradouro, uf, regiao } = cepInfo.data;
      formikRef.current.setFieldValue("state", uf);
      formikRef.current.setFieldValue("neighborhood", bairro);
      formikRef.current.setFieldValue("city", regiao);
      formikRef.current.setFieldValue("street", logradouro);
    }
  }, [cepInfo?.data]);

  return (
    <Formik
      initialValues={{ ...initialValues, cep }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      innerRef={formikRef}
    >
      {({
        setFieldValue,
        values,
        handleChange,
        errors,
        touched,
        isSubmitting,
      }) => {
        return (
          <ScheduleContainer className="formulario">
            <Box display="flex">
              {appointments && appointments?.length > 1 && (
                <Box
                  display="flex"
                  direction="column"
                  align="start"
                  mb="20"
                  className="schedule"
                  gap="0"
                >
                  <h1> Agenda selecionada</h1>
                  {appointmentSelected && <h2>{appointmentSelected?.name}</h2>}
                </Box>
              )}

              <Box ml="auto" display="flex" justify="end">
                <Button onClick={resetSchedule}>Voltar para CEP</Button>
              </Box>
            </Box>

            <div className="personal-info">
              <h1>Dados do cliente</h1>

              <div className="fields-container">
                <div className="lg-3">
                  <Field
                    id="name"
                    type="text"
                    component={Input}
                    label="Nome completo*"
                    value={values.name}
                    onChange={handleChange}
                    error={touched.name && errors.name}
                  />
                </div>

                <div className="lg-3">
                  <Field
                    id="email"
                    type="text"
                    component={Input}
                    label="E-mail*"
                    value={values.email}
                    onChange={handleChange}
                    error={touched.email && errors.email}
                  />
                </div>

                <div className="lg-3">
                  <Field
                    id="cpf"
                    type="text"
                    component={Input}
                    mask="999.999.999-99"
                    label="CPF*"
                    value={values.cpf}
                    onChange={handleChange}
                    error={touched.cpf && errors.cpf && errors.cpf}
                  />
                </div>

                <div className="lg-3">
                  <Field
                    id="birthDate"
                    type="text"
                    component={Input}
                    mask="99/99/9999"
                    label="Data de nascimento*"
                    value={values.birthDate}
                    onChange={handleChange}
                    error={touched.birthDate && errors.birthDate}
                  />
                </div>

                <div className="lg-3">
                  <Field
                    id="phone"
                    component={InputPhone}
                    placeholder="(99) 99999-9999"
                    label="Telefone*"
                    value={values.phone}
                    onChange={(phone: string) => setFieldValue("phone", phone)}
                    error={touched.phone && errors.phone}
                  />
                </div>

                <div className="lg-3">
                  <Field
                    id="weight"
                    type="text"
                    component={Input}
                    label="Peso"
                    value={values.weight}
                    onChange={handleChange}
                    error={touched.weight && errors.weight && errors.weight}
                  />
                </div>

                <div className="lg-3">
                  <Field
                    id="height"
                    type="text"
                    component={Input}
                    label="Altura"
                    value={values.height}
                    onChange={handleChange}
                    error={touched.height && errors.height}
                  />
                </div>

                <div className="lg-3">
                  <Field
                    name="gender"
                    id="gender"
                    label="GÊNERO*"
                    options={genders}
                    value={values.gender}
                    optionName="sub_gender"
                    readOnly={!validateArrayType(genders)}
                    onChange={handleChange}
                    component={Select}
                    placeholder="Selecione o gênero"
                    error={touched.gender && errors.gender && errors.gender}
                  />
                </div>

                <div className="lg-3">
                  <Field
                    id="motherName"
                    type="text"
                    component={Input}
                    label="Nome da mãe"
                    value={values.motherName}
                    onChange={handleChange}
                    error={touched.motherName && errors.motherName}
                  />
                </div>

                <div className="lg-12">
                  <Field
                    id="obs"
                    multiline
                    type="text"
                    component={Input}
                    label="Observação"
                    value={values.obs}
                    onChange={handleChange}
                  />
                </div>

                <div className="upload-file ">
                  <label htmlFor="fileInput" style={{ cursor: "pointer" }}>
                    <CampFile
                      className={
                        touched.attach && errors.attach ? "invalid" : ""
                      }
                    >
                      Pedido médico
                      <img src={ImportDocument} alt="" />
                      <input
                        type="file"
                        id="fileInput"
                        name="fileInput"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          addAttachment(e, setFieldValue)
                        }
                        style={{ display: "none" }}
                      />
                    </CampFile>
                  </label>
                  <ErrorMessage
                    name="attach"
                    component="div"
                    className="error-message"
                  />
                </div>

                <div className="preview-imgs lg-12">
                  {imagesPreview &&
                    imagesPreview.map((preview, index) => (
                      <div className="preview-img">
                        <img
                          src={preview}
                          alt="Preview"
                          style={{ width: 200, borderRadius: 8 }}
                        />
                        <Button
                          textButton
                          onClick={() => deleteFiles(index, setFieldValue)}
                        >
                          <img src={TrashIcon} className="icon" />
                        </Button>
                      </div>
                    ))}
                </div>
              </div>
            </div>

            <div className="address-info">
              <h1>Dados do endereço para atendimento</h1>

              <div className="fields-container">
                <div className="lg-3">
                  <Field
                    id="cep"
                    type="text"
                    component={Input}
                    mask="99999-999"
                    label="CEP*"
                    value={values.cep}
                    readOnly
                    error={touched.cep && errors.cep}
                  />
                </div>

                <div className="lg-3">
                  <Field
                    id="street"
                    type="text"
                    component={Input}
                    label="Rua*"
                    value={values.street}
                    onChange={handleChange}
                    error={touched.street && errors.street}
                  />
                </div>

                <div className="lg-3">
                  <Field
                    id="number"
                    type="text"
                    component={Input}
                    label="Número*"
                    value={values.number}
                    onChange={handleChange}
                    error={touched.number && errors.number}
                  />
                </div>

                <div className="lg-3">
                  <Field
                    id="complement"
                    type="text"
                    component={Input}
                    label="Complemento"
                    value={values.complement}
                    onChange={handleChange}
                    error={touched.complement && errors.complement}
                  />
                </div>

                <div className="lg-3">
                  <Field
                    id="neighborhood"
                    type="text"
                    component={Input}
                    label="Bairro*"
                    value={values.neighborhood}
                    onChange={handleChange}
                    error={touched.neighborhood && errors.neighborhood}
                  />
                </div>

                <div className="lg-3">
                  <Field
                    id="city"
                    type="text"
                    component={Input}
                    label="Cidade*"
                    value={values.city}
                    onChange={handleChange}
                    error={touched.city && errors.city}
                  />
                </div>

                <div className="lg-3">
                  <Field
                    id="state"
                    type="text"
                    component={Input}
                    label="Estado (UF)*"
                    value={values.state}
                    onChange={handleChange}
                    error={touched.state && errors.state}
                  />
                </div>

                <Box mt="30" />
              </div>
            </div>

            <Box mt="20" />

            <Button
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              Enviar
            </Button>
          </ScheduleContainer>
        );
      }}
    </Formik>
  );
};
